import React, { useState, useEffect } from 'react';
import './GbpMappingPage.css';
const GbpMappingPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [sessionData, setSessionData] = useState({
        brand_id: null,
        brand_name: '',
        connected_email: '',
        businesses: [],
        locations: []
    });

    // Object mapping business_id -> location_id
    const [selectedMappings, setSelectedMappings] = useState({});

    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.staging.waitwhiz.in';

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryError = urlParams.get('error');
        const sessionId = urlParams.get('session_id');

        if (queryError) {
            let errorMsg = 'Google authentication failed or was cancelled.';
            if (queryError === 'missing_refresh_token') {
                errorMsg = 'Google did not return a refresh token. Please re-authorize and grant permissions.';
            } else if (queryError === 'invalid_state_token') {
                errorMsg = 'Security validation failed (invalid or expired state token). Please try connecting again.';
            } else if (queryError === 'token_exchange_failed') {
                errorMsg = 'Failed to exchange authorization code with Google. Please try again.';
            }
            setError(errorMsg);
            setLoading(false);
            return;
        }

        if (!sessionId) {
            setError('No mapping session ID provided.');
            setLoading(false);
            return;
        }

        const fetchSessionInfo = async () => {
            try {
                const response = await fetch(`${apiUrl}/business/gbp/mapping-session/${sessionId}`);
                if (!response.ok) {
                    const errJson = await response.json().catch(() => ({}));
                    throw new Error(errJson.detail || 'Mapping session expired or invalid.');
                }
                const resJson = await response.json();
                const data = resJson.data || resJson;
                setSessionData(data);

                // Initial mapping logic
                const initialMap = {};
                const locations = data.locations || [];
                const businesses = data.businesses || [];

                businesses.forEach((biz) => {
                    if (biz.current_location_id && locations.some(l => l.location_id === biz.current_location_id)) {
                        initialMap[biz.id] = biz.current_location_id;
                    } else if (businesses.length === 1 && locations.length === 1) {
                        initialMap[biz.id] = locations[0].location_id;
                    } else {
                        initialMap[biz.id] = '';
                    }
                });

                setSelectedMappings(initialMap);
            } catch (err) {
                console.error('Error loading GBP mapping session:', err);
                setError(err.message || 'Failed to load Google Business Profile mapping session.');
            } finally {
                setLoading(false);
            }
        };

        fetchSessionInfo();
    }, [apiUrl]);

    const handleSelectChange = (businessId, locationId) => {
        setSelectedMappings(prev => ({
            ...prev,
            [businessId]: locationId
        }));
    };

    const handleSaveMappings = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        if (!sessionId) {
            setError('Session ID missing.');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            // Build array of mappings
            const mappingList = [];
            const locations = sessionData.locations || [];

            Object.entries(selectedMappings).forEach(([bizIdStr, locId]) => {
                if (locId) {
                    const matchedLoc = locations.find(l => l.location_id === locId);
                    if (matchedLoc) {
                        mappingList.push({
                            business_id: parseInt(bizIdStr, 10),
                            location_id: matchedLoc.location_id,
                            account_id: matchedLoc.account_id,
                            location_name: matchedLoc.location_name
                        });
                    }
                }
            });

            if (mappingList.length === 0) {
                const confirmEmpty = window.confirm("No businesses are selected to map. Are you sure you want to proceed?");
                if (!confirmEmpty) {
                    setSaving(false);
                    return;
                }
            }

            const response = await fetch(`${apiUrl}/business/gbp/save-mapping`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    mappings: mappingList
                })
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || 'Failed to save Google Business Profile mappings.');
            }

            setSuccessMessage('Google Business Profile mapped successfully!');

            // Notify opener tab safely
            if (window.opener && !window.opener.closed) {
                try {
                    window.opener.postMessage({ type: 'GBP_OAUTH_SUCCESS' }, window.location.origin);
                } catch (e) {
                    console.error('Failed to post message to opener window:', e);
                }
                setTimeout(() => {
                    window.close();
                }, 1500);
            } else {
                setTimeout(() => {
                    window.location.href = '/business-settings';
                }, 2000);
            }
        } catch (err) {
            console.error('Error saving GBP mappings:', err);
            setError(err.message || 'Failed to save location mappings.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="gbp-page-container">
                <div className="gbp-card gbp-loading-card">
                    <div className="gbp-spinner"></div>
                    <h2>Loading Google Business Locations...</h2>
                    <p>Fetching account locations from Google Business Profile API.</p>
                </div>
            </div>
        );
    }

    if (error && !sessionData.brand_id) {
        return (
            <div className="gbp-page-container">
                <div className="gbp-card gbp-error-card">
                    <div className="gbp-icon-badge gbp-error-badge">⚠️</div>
                    <h2>Connection Error</h2>
                    <p className="gbp-error-text">{error}</p>
                    <button
                        type="button"
                        className="gbp-btn gbp-btn-primary"
                        onClick={() => {
                            if (window.opener && !window.opener.closed) {
                                window.close();
                            } else {
                                window.location.href = '/business-settings';
                            }
                        }}
                    >
                        Close & Return to Settings
                    </button>
                </div>
            </div>
        );
    }

    const locations = sessionData.locations || [];
    const businesses = sessionData.businesses || [];

    return (
        <div className="gbp-page-container">
            <div className="gbp-card">
                {/* Header Section */}
                <div className="gbp-header">
                    <div className="gbp-header-top">
                        <div className="gbp-brand-tag">
                            <span className="gbp-google-icon">🌐</span> Google Business Profile Integration
                        </div>
                        {sessionData.connected_email && (
                            <div className="gbp-email-badge">
                                👤 {sessionData.connected_email}
                            </div>
                        )}
                    </div>
                    <h1>{sessionData.brand_name ? `${sessionData.brand_name} - Location Mapping` : 'Map Business Locations'}</h1>
                    <p>Connect your WaitWhiz business locations to your Google Business Profile pages for automated reviews and local posts.</p>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="gbp-alert gbp-alert-danger">
                        <span>⚠️ {error}</span>
                    </div>
                )}

                {/* Success Banner */}
                {successMessage && (
                    <div className="gbp-alert gbp-alert-success">
                        <span>✅ {successMessage}</span>
                    </div>
                )}

                {/* Empty Locations Warning */}
                {locations.length === 0 ? (
                    <div className="gbp-empty-state">
                        <div className="gbp-empty-icon">📍</div>
                        <h3>No Google Business Profile Locations Found</h3>
                        <p>
                            No active Google Business Profile locations were found under account <strong>{sessionData.connected_email}</strong> across personal or organization accounts.
                        </p>
                        <p className="gbp-subhint">
                            Please verify that your Google account has owner or manager access to your Google Business Profile listings, then try connecting again.
                        </p>
                        <div style={{ marginTop: '16px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                className="gbp-btn gbp-btn-secondary"
                                onClick={() => {
                                    if (window.opener && !window.opener.closed) {
                                        window.close();
                                    } else {
                                        window.location.href = '/business-settings';
                                    }
                                }}
                            >
                                Close & Return
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Business Mapping List */}
                        <div className="gbp-mapping-section">
                            <h3>Select Location Mappings</h3>
                            <p className="gbp-section-desc">Map each WaitWhiz location name to its official Google Maps business profile listing.</p>

                            <div className="gbp-business-list">
                                {businesses.map((biz) => {
                                    const currentSelected = selectedMappings[biz.id] || '';
                                    return (
                                        <div key={biz.id} className="gbp-business-item">
                                            <div className="gbp-biz-info">
                                                <div className="gbp-biz-name">🏢 {biz.name}</div>
                                                {biz.location && <div className="gbp-biz-location">📍 {biz.location}</div>}
                                            </div>
                                            <div className="gbp-biz-selector">
                                                <select
                                                    className="gbp-select"
                                                    value={currentSelected}
                                                    onChange={(e) => handleSelectChange(biz.id, e.target.value)}
                                                >
                                                    <option value="">-- Do Not Map / Select Google Location --</option>
                                                    {locations.map((loc) => (
                                                        <option key={loc.location_id} value={loc.location_id}>
                                                            {loc.location_name} {loc.address ? `(${loc.address})` : ''} [{loc.account_name}]
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Action Footer */}
                        <div className="gbp-footer-actions">
                            <button
                                type="button"
                                className="gbp-btn gbp-btn-secondary"
                                onClick={() => {
                                    if (window.opener && !window.opener.closed) {
                                        window.close();
                                    } else {
                                        window.location.href = '/business-settings';
                                    }
                                }}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="gbp-btn gbp-btn-primary"
                                onClick={handleSaveMappings}
                                disabled={saving}
                            >
                                {saving ? 'Saving Mappings...' : 'Save & Finish Mapping'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default GbpMappingPage;
